import { Entity, EntityId } from '@marmon/shared/types/common';
import { Quad, QuadEightyTwenty } from '@marmon/shared/types/quad';
import { Quartile } from '@marmon/shared/types/quartile';

export const CalculationTypes = ['revenue', 'margin'] as const;
export type CalculationType = (typeof CalculationTypes)[number];

export const DefaultCalculations = ['revenue', 'margin'];
export type DefaultCalculation = (typeof DefaultCalculations)[number];
export type CalculationId = DefaultCalculation | EntityId;

/**
 * Calculations that are bucketed by the quad they have been determined in
 */
export type QuadCalculation<T = number> = { [key in Quad]: T };
export const DefaultQuadCalculation = (): QuadCalculation => ({
	1: 0,
	2: 0,
	3: 0,
	4: 0,
});

export const DefaultQuadMarginDistributionCalculation =
	(): QuadCalculation<MarginDistributionCalculation> => ({
		1: { revenueInCents: 0, marginInCents: 0 },
		2: { revenueInCents: 0, marginInCents: 0 },
		3: { revenueInCents: 0, marginInCents: 0 },
		4: { revenueInCents: 0, marginInCents: 0 },
	});

/**
 * Calculations that are bucketed by the quartile they have been determined in
 */
export type QuartileCalculation<T = number> = { [key in Quartile]: T };
export const DefaultQuartileCalculation = (): QuartileCalculation => ({
	1: 0,
	2: 0,
	3: 0,
	4: 0,
});

export const DefaultQuartileMarginDistributionCalculation =
	(): QuartileCalculation<MarginDistributionCalculation> => ({
		1: { revenueInCents: 0, marginInCents: 0 },
		2: { revenueInCents: 0, marginInCents: 0 },
		3: { revenueInCents: 0, marginInCents: 0 },
		4: { revenueInCents: 0, marginInCents: 0 },
	});

/**
 * Save information needed to calculate the margin distribution per each quad
 */
export type MarginDistributionCalculation = {
	revenueInCents: number;
	marginInCents: number;
};

/**
 * Calculations that involve a distribution against an entity involved such as
 * products or customers. The distribution is against the EightyTwenty model with
 * key 80's and other 20's.
 */
export type EightyTwentyCalculation = {
	[key in QuadEightyTwenty]: number;
};
export const DefaultEightyTwentyCalculation = (): EightyTwentyCalculation => ({
	'80': 0,
	'20': 0,
});

/**
 * Note that an analysis calculation is based on the segment and calculation type
 * of the global filters. If either of those change then a new analysis will need
 * to be generated. Filtering by products and customers do not result in new
 * calculations.
 */
interface AnalysisCalculationBase extends Entity {
	id: CalculationId;
	type: CalculationType;

	/**
	 * The version of calculation logic that was ran.
	 */
	version: number;

	totalInCents: number;

	totalProducts: number;
	totalCustomers: number;

	/**
	 * essentially the total invoices per quad
	 */
	modeledOverheadDistribution: QuadCalculation;

	/**
	 * how the revenue is distributed across the quads
	 */
	revenueDistribution: QuadCalculation;

	/**
	 * how the margin is distributed across the quads
	 */
	marginDistribution: QuadCalculation<MarginDistributionCalculation>;

	/**
	 * each quads average transaction value
	 */
	averageTransactionValue: QuadCalculation;

	/**
	 * the product distribution between the 80's and 20's
	 */
	productDistribution: EightyTwentyCalculation;

	/**
	 * the customer distribution between the 80's and 20's
	 */
	customerDistribution: EightyTwentyCalculation;

	customerRevenue: QuartileCalculation;
	customerMargin: QuartileCalculation<MarginDistributionCalculation>;
	customerAverageTransaction: QuartileCalculation;

	productRevenue: QuartileCalculation;
	productMargin: QuartileCalculation<MarginDistributionCalculation>;
	productAverageTransaction: QuartileCalculation;

	/**
	 * The segment sequence is our bread and butter on how we differentiate the calculations
	 * based on the currently active segments being applied to the data set. We will sort all
	 * of the segments by alphabetical order, and then grab the index of each active segment
	 * that is being applied to the data. Then we will encrypt it and generate a string that
	 * we can store here and query against for future determining.
	 */
	segmentSequence: string | null;

	/**
	 * The number of plots that this calculation is associated with.
	 */
	plotCount: number;

	/**
	 * The number of transactions that are included in this analysis
	 */
	transactionCount: number;
}

export type AnalysisCalculation = AnalysisCalculationBase;

/**
 * The default type of our calculations
 */
export const DefaultCalculationType: CalculationType = 'revenue';

export interface AnalysisCalculationPayload {
	segmentSequence: string;
	type: CalculationType;
}
